import type { Checkout } from '@givebacks/api/store';

type CheckoutChannel = 'web' | 'native';

type CheckoutOrganization = {
  id: string;
  name?: string;
};

type CheckoutOrganizationNamesById = Record<string, string | undefined>;

type TrackPendoCheckoutCompletedArgs = {
  checkout?: Checkout | null;
  channel: CheckoutChannel;
  organizationNamesById?: CheckoutOrganizationNamesById;
};

type PendoApi = {
  track?: (eventName: string, eventProperties: unknown) => void;
};

type CheckoutCartWithCause = Checkout['carts'][number] & {
  cause?: {
    name?: string | null;
  };
};

const getPendoApi = (): PendoApi | undefined => {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { pendo?: PendoApi }).pendo;
};

const getCauseName = (cart: Checkout['carts'][number]) => {
  return (cart as CheckoutCartWithCause).cause?.name?.trim();
};

/**
 * Builds a deduped organization list from checkout carts.
 * We key by cause id so the same organization is only sent once.
 */
const getCheckoutOrganizationsFromCheckout = (
  checkout: Checkout,
  organizationNamesById: CheckoutOrganizationNamesById = {}
): CheckoutOrganization[] => {
  const organizationsMap = new Map<string, CheckoutOrganization>();

  checkout.carts.forEach((cart) => {
    const id = cart.cause_id;

    if (!id) {
      return;
    }

    // Prefer the name from query/cache. Fallback to checkout cart cause name.
    const name = organizationNamesById[id]?.trim() || getCauseName(cart);
    const existingOrganization = organizationsMap.get(id);

    if (!existingOrganization) {
      organizationsMap.set(
        id,
        name
          ? {
              id,
              name,
            }
          : { id }
      );
      return;
    }

    // If we first saw this org without a name, fill it when we later find one.
    if (!existingOrganization.name && name) {
      organizationsMap.set(id, {
        ...existingOrganization,
        name,
      });
    }
  });

  return Array.from(organizationsMap.values());
};

const trackPendoCheckoutCompleted = ({
  checkout,
  channel,
  organizationNamesById,
}: TrackPendoCheckoutCompletedArgs) => {
  // Only track completed purchases with a stable checkout id.
  if (!checkout?.uuid || checkout.status !== 'purchased') {
    return false;
  }

  try {
    // Keep all tracking preparation in this try block.
    // Tracking must never break checkout success UX.
    const organizations = getCheckoutOrganizationsFromCheckout(checkout, organizationNamesById);

    const nonDevEnvironments =
      import.meta.env.VITE_API_ENV === 'production' ? 'production' : 'staging';

    // eslint-disable-next-line turbo/no-undeclared-env-vars
    const trackingEnv = import.meta.env.DEV ? 'development' : nonDevEnvironments;

    const pendoPayload = {
      uuid: checkout.uuid,
      channel,
      total: checkout.total,
      subtotal: checkout.subtotal,
      organizations,
      organization_ids: organizations.map((organization) => organization.id),
      environment: trackingEnv,
    };

    const pendoApi = getPendoApi();
    pendoApi?.track?.('Checkout Completed', pendoPayload);
  } catch {
    // no-op: analytics tracking must never block checkout success
    return false;
  }

  return true;
};

export { trackPendoCheckoutCompleted };
export type {
  CheckoutChannel,
  CheckoutOrganization,
  CheckoutOrganizationNamesById,
  TrackPendoCheckoutCompletedArgs,
};
